import React, { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { FormBlock, StepProps } from '@/widgets/ServiceFormTemplate';

import { buildDocumentPreviewRows } from '@/features/ui/DocumentsData/model/preview';

import { IntegrationSystem } from '@/shared/types';
import { ConditionView, FormSelect, Grid, Loader, StepControlPanel } from '@/shared/ui';

import { useAsyncDataLoader } from '@/hooks/useAsyncDataLoader';

import { getERZDocumentsSelect } from '@/modules/erz/helpers';

import { DocumentTypeCode, DocumentTypeEntity } from '@/api/types';

import { DocumentsData } from '../../DocumentsData';
import { YesNoSelectOptions } from '../../ProfileChapters';

import { LearningDocumentSchema } from './model/types';

export interface LearningDocumentProps {
  isLearningDocumentStepMandatory?: boolean;
  learningDocumentCustomNextStepHandler?: (formData: any, specificStep: (step: number) => void) => () => Promise<void>;
}

export const LearningDocument: React.FC<StepProps<LearningDocumentProps>> = ({
  currentRequestId,
  prev,
  next,
  specificStepWithValidation,
  onStepDraftSave,
  isLearningDocumentStepMandatory = false,
  learningDocumentCustomNextStepHandler,
}) => {
  const { control, watch, setValue, getValues } = useFormContext<LearningDocumentSchema>();

  const learningDoc = watch('learningDoc');
  const learningDocument = watch('learningDocument');

  const fetchDocumentTypes = useCallback(
    () =>
      getERZDocumentsSelect({
        allowedDocumentTypeCodes: [DocumentTypeCode.TECHNICAL_REFERENCE],
      }),
    [],
  );

  const {
    data: documentTypeEntities = [],
    isLoading: isTechnicalReferenceLoading,
    load: loadDocumentTypeEntities,
  } = useAsyncDataLoader<Array<DocumentTypeEntity<IntegrationSystem.ERZ>>>(fetchDocumentTypes, {
    defaultDataValue: [],
  });

  const safeRequestId = currentRequestId ?? '';

  useEffect(() => {
    loadDocumentTypeEntities();
    setValue('learningDoc', isLearningDocumentStepMandatory ? YesNoSelectOptions.NO : YesNoSelectOptions.YES);
  }, []);

  useEffect(() => {
    !Number(learningDoc) && !learningDocument?.length && setValue('learningDocument', []);
  }, [learningDoc]);

  return (
    <ConditionView if={!isTechnicalReferenceLoading} else={<Loader />}>
      <FormBlock title="Дані про вивчення матеріальної частини зброї, правил поводження та застосування">
        {isLearningDocumentStepMandatory && (
          <FormSelect
            name="learningDoc"
            control={control}
            label="Ви хочете додати дані документа?"
            options={Object.values(YesNoSelectOptions)}
          />
        )}
        {Number(learningDoc?.id) ? (
          <DocumentsData
            withUploader
            documentsRequired
            documentTypeSelectDisabled
            documentTypeEntities={documentTypeEntities ?? []}
            maxDocumentsCount={1}
            name="learningDocument"
            request_id={safeRequestId}
            onSaveDraft={onStepDraftSave}
          />
        ) : (
          <></>
        )}
      </FormBlock>
      <StepControlPanel
        prev={prev}
        next={
          learningDocumentCustomNextStepHandler
            ? learningDocumentCustomNextStepHandler(getValues(), specificStepWithValidation)
            : next
        }
      />
    </ConditionView>
  );
};

export const LearningDocumentPreview: React.FC = () => {
  const { getValues } = useFormContext<LearningDocumentSchema>();
  const { learningDocument } = getValues();
  const doc = Array.isArray(learningDocument) ? learningDocument[0] : undefined;
  const rows = buildDocumentPreviewRows(doc, 'Документ, який підтверджує вивчення');

  return rows.length ? (
    <Grid title="Дані про вивчення матеріальної частини зброї, правил поводження та застосування" data={rows} />
  ) : null;
};
